import React, { useMemo, useState } from "react";
import { Layers, Plus } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle, ConfirmModal,
} from "../../ui";
import SectionCard from "./SectionCard";
import QuestionPickerDialog from "./QuestionPickerDialog";
import CodingPickerDialog from "./CodingPickerDialog";
import { blankSection, codingOnPaper, letterOf, questionsOnPaper } from "./builderRules";

/**
 * Step 3 — the paper: its sections and what is in each.
 *
 * A one-part assessment shows one card with no name; "Split into sections"
 * names it Section A and adds Section B. Removing a section that has anything
 * in it is confirmed — its questions leave the paper, never the bank.
 */
export default function QuestionsStep({
  form, setForm, categories, coding, problems, locked, codingBankPath,
}) {
  const [picker, setPicker] = useState(null); // { kind: 'questions' | 'coding', key }
  const [removing, setRemoving] = useState(null);

  const updateSection = (key, patch) => setForm((f) => ({
    ...f,
    sections: f.sections.map((s) => (s.key === key ? { ...s, ...patch } : s)),
  }));

  const addSection = () => setForm((f) => ({
    ...f,
    sectioned: true,
    sections: [
      ...f.sections.map((s, i) => (f.sectioned || s.title ? s : { ...s, title: `Section ${letterOf(i)}` })),
      blankSection({ title: `Section ${letterOf(f.sections.length)}` }),
    ],
  }));

  const move = (index, dir) => setForm((f) => {
    const next = [...f.sections];
    const to = index + dir;
    if (to < 0 || to >= next.length) return f;
    [next[index], next[to]] = [next[to], next[index]];
    return { ...f, sections: next };
  });

  const remove = (key) => setForm((f) => {
    const sections = f.sections.filter((s) => s.key !== key);
    return { ...f, sections: sections.length ? sections : [blankSection()], ...(sections.length < 2 ? { timing: "overall" } : {}) };
  });

  const askRemove = (section) => {
    const has = (section.questions || []).length + (section.rules || []).length + (section.coding || []).length;
    if (has) setRemoving(section);
    else remove(section.key);
  };

  const mergeIntoOnePart = () => setForm((f) => ({ ...f, sectioned: false, timing: "overall" }));

  const onPaper = useMemo(() => questionsOnPaper(form), [form]);
  const codingPaper = useMemo(() => codingOnPaper(form), [form]);
  const pickerSection = picker ? form.sections.find((s) => s.key === picker.key) : null;
  const pickerIndex = pickerSection ? form.sections.indexOf(pickerSection) : -1;
  const pickerName = pickerSection
    ? (pickerSection.title?.trim() || (form.sectioned ? `Section ${letterOf(pickerIndex)}` : "this assessment"))
    : "";
  // Everything on the paper except the section being picked for.
  const elsewhere = (map) => {
    const out = new Map(map);
    for (const [id, where] of map) if (where.key === picker?.key) out.delete(id);
    return out;
  };

  const problemsOf = (key) => problems.filter((p) => p.section === key).map((p) => p.message);
  const general = problems.filter((p) => !p.section);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Add questions{form.sectioned ? " to each section" : ""}</AppCardTitle>
          <AppCardDescription>
            Pick questions one by one from your question bank, or give each student a random draw.
            {coding.allowed ? " A coding section takes programming problems from your coding bank." : ""}
          </AppCardDescription>
        </AppCardHeader>
        {!form.sectioned && (
          <AppCardContent className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
            <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
              Need parts like <em>Aptitude</em>, <em>{coding.shown ? "Coding" : "Reasoning"}</em> and <em>Technical</em>? Split it into sections —
              each can have its own questions and its own timer.
            </p>
            <AppButton type="button" size="sm" variant="outline" onClick={addSection} disabled={locked}>
              <Layers size={16} aria-hidden="true" /> Split into sections
            </AppButton>
          </AppCardContent>
        )}
      </AppCard>

      {general.length > 0 && general.map((p) => (
        <p key={p.message} className="tw-m-0 tw-text-[0.8125rem] tw-text-danger" role="alert">{p.message}</p>
      ))}

      {form.sections.map((s, i) => (
        <SectionCard
          key={s.key}
          section={s}
          index={i}
          count={form.sections.length}
          sectioned={form.sectioned}
          categories={categories}
          coding={coding}
          problems={problemsOf(s.key)}
          locked={locked}
          onChange={(patch) => updateSection(s.key, patch)}
          onMove={(dir) => move(i, dir)}
          onRemove={() => askRemove(s)}
          onPickQuestions={() => setPicker({ kind: "questions", key: s.key })}
          onPickCoding={() => setPicker({ kind: "coding", key: s.key })}
        />
      ))}

      {form.sectioned && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <AppButton type="button" variant="outline" onClick={addSection} disabled={locked}>
            <Plus size={16} aria-hidden="true" /> Add section {letterOf(form.sections.length)}
          </AppButton>
          {form.sections.length === 1 && !(form.sections[0].coding || []).length && (
            <AppButton type="button" variant="ghost" onClick={mergeIntoOnePart} disabled={locked}>
              Use one part instead
            </AppButton>
          )}
        </div>
      )}

      <QuestionPickerDialog
        open={picker?.kind === "questions" && !!pickerSection}
        onClose={() => setPicker(null)}
        sectionName={pickerName}
        current={pickerSection?.questions || []}
        elsewhere={picker?.kind === "questions" ? elsewhere(onPaper) : null}
        categories={categories}
        onConfirm={(questions) => {
          updateSection(picker.key, { questions, source: "pick" });
          setPicker(null);
        }}
      />

      <CodingPickerDialog
        open={picker?.kind === "coding" && !!pickerSection}
        onClose={() => setPicker(null)}
        sectionName={pickerName}
        current={pickerSection?.coding || []}
        elsewhere={picker?.kind === "coding" ? elsewhere(codingPaper) : null}
        bankPath={codingBankPath}
        onConfirm={(codingRows) => {
          // A one-part paper with a coding problem is saved as one section
          // (builderRules.needsSections); the screen keeps showing one part.
          updateSection(picker.key, { coding: codingRows });
          setPicker(null);
        }}
      />

      <ConfirmModal
        show={!!removing}
        onHide={() => setRemoving(null)}
        onConfirm={() => { remove(removing.key); setRemoving(null); }}
        title={`Remove ${removing?.title?.trim() || "this section"}?`}
        message={coding.shown
          ? "Its questions and coding problems are taken off this assessment. They stay in your question and coding banks."
          : "Its questions are taken off this assessment. They stay in your question bank."}
        confirmText="Remove section"
        variant="danger"
      />
    </div>
  );
}
